// favourites.ts — the user's own ★ Фавориты, read off Digitain's own client.
//
// Measured live on parikara.com, 2026-08-08 (logged-in account, RU board). The
// operator's header has a ★ Фавориты section with three tabs, and each one is
// served by its own call on the SAME RequestHelper channel the odds feed uses
// (`{sender:"http-api-response", url, res}`):
//
//   /Account/GetUserFavoriteCompetitors  → starred TEAMS
//        [{ SportId, SportName, SportEnglishName, Count,
//           UserCompetitorItems:[{ CompetitorId, CompetitorName,
//                                  CompetitorEnglishName, LogoId, Logo, EventId }] }]
//   /Account/GetFavoriteTournament       → starred LEAGUES
//        [{ Id, SN, EGN, CH:[{ Id, CN, EGN, FId, IsL, CI, ECtN }] }]
//   /Account/GetAllFavoriteEvents        → starred FIXTURES (full event objects,
//        same shape as the live feed: Id, HT/EHT, AT/EAT, CN/ECN, StakeTypes…)
//
// ⚠️ These three are AUTHENTICATED — the outgoing message carries `auth:true` and
// an Authorization JWT that lives only in the operator's own JS memory (it is in
// no cookie and no storage; verified). Our captured feed template is anonymous, so
// replaying it at an `/Account/*` path would send no token. Hence this module is
// PASSIVE ONLY: it reads the responses when the user's own client makes the calls
// (i.e. when they open ★ Фавориты), and caches what it saw. Nothing is requested.
//
// Because a passive read only happens when the user visits that tab, the snapshot
// is PERSISTED — one visit, ever, keeps steering their recommendations for 30 days.
//
// ⚠️ Digitain sends every name twice (localized + always-English), and the
// favourites payloads are no exception (`CompetitorName` / `CompetitorEnglishName`,
// `CN` / `EGN`). We keep BOTH: the backend matches English↔English where the board
// exposes English team names, and falls back to localized↔localized, which is sound
// here because both sides come from the same feed under the same langId.

const STORE_PREFIX = "bc_fav_v1:";
/** A starred team is a taste signal, not a session fact — but a year-old star for
 *  a club the user stopped following is noise. 30 days is the compromise, and a
 *  fresh visit to the tab renews it. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Caps: this rides on every /suggestions POST. A user with 400 starred teams must
 *  not turn a 2KB request into a 40KB one. */
const MAX_TEAMS = 40;
const MAX_LEAGUES = 40;
const MAX_EVENTS = 40;

export interface FavouriteTeam {
  id: number;
  /** Localized (the board's language) — display + fallback matching. */
  name: string;
  /** Always-English — the name ranking matches on. */
  name_en: string;
  sport_en: string;
}

export interface FavouriteLeague {
  id: number;
  name: string;
  name_en: string;
  country_en: string;
  sport_en: string;
}

export interface FavouriteEvent {
  event_id: number;
  home: string;
  away: string;
  home_en: string;
  away_en: string;
  league_en: string;
}

export interface FavouritesSnapshot {
  teams: FavouriteTeam[];
  leagues: FavouriteLeague[];
  events: FavouriteEvent[];
  /** ms epoch of the most recent capture in this snapshot. */
  captured_at: number;
}

interface StoredSection<T> { items: T[]; ts: number }
interface Stored {
  teams?: StoredSection<FavouriteTeam>;
  leagues?: StoredSection<FavouriteLeague>;
  events?: StoredSection<FavouriteEvent>;
}

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const num = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** `/Account/GetUserFavoriteCompetitors` → teams. Grouped by sport. */
export function parseFavouriteTeams(res: unknown): FavouriteTeam[] {
  if (!Array.isArray(res)) return [];
  const out: FavouriteTeam[] = [];
  for (const raw of res) {
    if (!raw || typeof raw !== "object") continue;
    const group = raw as Record<string, unknown>;
    const sportEn = str(group.SportEnglishName) || str(group.SportName);
    const items = group.UserCompetitorItems;
    if (!Array.isArray(items)) continue;
    for (const rawItem of items) {
      if (!rawItem || typeof rawItem !== "object") continue;
      const it = rawItem as Record<string, unknown>;
      const id = num(it.CompetitorId);
      const nameEn = str(it.CompetitorEnglishName);
      const name = str(it.CompetitorName);
      if (id == null || (!nameEn && !name)) continue;
      out.push({ id, name: name || nameEn, name_en: nameEn || name, sport_en: sportEn });
      if (out.length >= MAX_TEAMS) return out;
    }
  }
  return out;
}

/** `/Account/GetFavoriteTournament` → leagues. Grouped by sport, `CH` holds the champs. */
export function parseFavouriteLeagues(res: unknown): FavouriteLeague[] {
  if (!Array.isArray(res)) return [];
  const out: FavouriteLeague[] = [];
  for (const raw of res) {
    if (!raw || typeof raw !== "object") continue;
    const group = raw as Record<string, unknown>;
    // The GROUP's EGN is the sport ("Football"); each CH entry's EGN is the league.
    const sportEn = str(group.EGN) || str(group.SN);
    const champs = group.CH;
    if (!Array.isArray(champs)) continue;
    for (const rawCh of champs) {
      if (!rawCh || typeof rawCh !== "object") continue;
      const ch = rawCh as Record<string, unknown>;
      const id = num(ch.Id);
      const nameEn = str(ch.EGN);
      const name = str(ch.CN);
      if (id == null || (!nameEn && !name)) continue;
      out.push({
        id,
        name: name || nameEn,
        name_en: nameEn || name,
        country_en: str(ch.ECtN),
        sport_en: sportEn,
      });
      if (out.length >= MAX_LEAGUES) return out;
    }
  }
  return out;
}

/** `/Account/GetAllFavoriteEvents` → fixtures. Grouped by sport, `Events` holds
 *  full event objects (the same shape the live feed sends). */
export function parseFavouriteEvents(res: unknown): FavouriteEvent[] {
  if (!Array.isArray(res)) return [];
  const out: FavouriteEvent[] = [];
  for (const raw of res) {
    if (!raw || typeof raw !== "object") continue;
    const group = raw as Record<string, unknown>;
    const events = group.Events;
    if (!Array.isArray(events)) continue;
    for (const rawEv of events) {
      if (!rawEv || typeof rawEv !== "object") continue;
      const ev = rawEv as Record<string, unknown>;
      const id = num(ev.Id);
      if (id == null) continue;
      out.push({
        home: str(ev.HT),
        away: str(ev.AT),
        home_en: str(ev.EHT) || str(ev.HT),
        away_en: str(ev.EAT) || str(ev.AT),
        league_en: str(ev.ECN) || str(ev.CN),
        event_id: id,
      });
      if (out.length >= MAX_EVENTS) return out;
    }
  }
  return out;
}

/**
 * Watches the RequestHelper channel for the three favourites responses, caches
 * what it sees, and hands the backend a snapshot on every /suggestions call.
 *
 * Storage is per API KEY. Two operators on one browser (which only happens on our
 * own demo pages, never in a real install) must not share a taste profile — and
 * more importantly, a key switch during testing must not silently inherit the
 * other operator's favourites and make a ranking change look like it worked.
 */
export class DigitainFavourites {
  private key: string;
  private data: Stored = {};
  private started = false;
  private messageListener: ((e: MessageEvent) => void) | null = null;
  /** Set once a live response has landed in THIS session — the diag strip needs
   *  to separate "read it just now" from "serving a 3-week-old cache". */
  private liveThisSession = false;
  /** Sections read live in THIS page session. ⛔ Only these are ever used: the
   *  owner changed his ★ on 2026-10-26 and a weeks-old cached team kept serving
   *  "starred_team" cards, because nothing refreshed the cache. */
  private live = new Set<keyof Stored>();
  private fetchedThisSession = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(apiKey: string) {
    this.key = STORE_PREFIX + (apiKey || "unknown");
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.load();
    try {
      this.messageListener = (e: MessageEvent) => this.onMessage(e);
      window.addEventListener("message", this.messageListener, false);
    } catch {
      /* no window — the snapshot stays whatever storage held */
    }
  }

  stop(): void {
    if (this.messageListener) {
      try { window.removeEventListener("message", this.messageListener); } catch { /* no window */ }
      this.messageListener = null;
    }
    this.started = false;
  }

  private onMessage(e: MessageEvent): void {
    if (!this.started) return;
    try {
      const d = e.data as Record<string, unknown> | null;
      if (!d || d.sender !== "http-api-response" || typeof d.url !== "string") return;
      const url = d.url as string;
      const res = d.res;
      if (url.indexOf("/Account/GetUserFavoriteCompetitors") >= 0) {
        this.put("teams", parseFavouriteTeams(res));
      } else if (url.indexOf("/Account/GetFavoriteTournament") >= 0) {
        this.put("leagues", parseFavouriteLeagues(res));
      } else if (url.indexOf("/Account/GetAllFavoriteEvents") >= 0) {
        this.put("events", parseFavouriteEvents(res));
      } else if (url.indexOf("/Account/") >= 0 && /favou?rite/i.test(url)) {
        // The operator's own ★ add/remove (team, league or match): re-read all three.
        this.scheduleRefresh();
      }
    } catch {
      /* malformed relay message */
    }
  }

  /**
   * Record one section. An EMPTY list is recorded too, and deliberately: the user
   * un-starring their last team is a real answer, and treating "empty" as "no
   * reading" would pin them to a favourite they explicitly removed.
   */
  private put(section: keyof Stored, items: unknown[]): void {
    (this.data as Record<string, unknown>)[section] = { items, ts: Date.now() };
    this.liveThisSession = true;
    this.live.add(section);
    this.save();
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Stored;
      if (parsed && typeof parsed === "object") this.data = parsed;
    } catch {
      this.data = {};
    }
  }

  private save(): void {
    try {
      localStorage.setItem(this.key, JSON.stringify(this.data));
    } catch {
      /* private mode / quota — the in-memory copy still serves this session */
    }
  }

  private fresh<T>(name: keyof Stored, section: StoredSection<T> | undefined): T[] {
    if (!this.live.has(name)) return [];
    if (!section || !Array.isArray(section.items)) return [];
    if (Date.now() - (section.ts || 0) > MAX_AGE_MS) return [];
    return section.items;
  }

  /** What to send with /suggestions. `null` when we have nothing — the backend
   *  then behaves exactly as it does today (no favourites block, no boost). */
  snapshot(): FavouritesSnapshot | null {
    this.maybeFetch();
    const teams = this.fresh("teams", this.data.teams);
    const leagues = this.fresh("leagues", this.data.leagues);
    const events = this.fresh("events", this.data.events);
    if (!teams.length && !leagues.length && !events.length) return null;
    const ts = Math.max(
      this.data.teams?.ts || 0,
      this.data.leagues?.ts || 0,
      this.data.events?.ts || 0,
    );
    return { teams, leagues, events, captured_at: ts };
  }

  /**
   * The transport for WRITING to the account surface. Injected by the Digitain boot
   * path so this module keeps no adapter import (mirrors `renderer.priceProbe`).
   * Null everywhere else, which makes `canStar()` false and the ★ never render.
   */
  account: {
    canCallAccount(): boolean;
    callAccount(path: string, data: Record<string, unknown>): Promise<unknown>;
  } | null = null;

  /** Event ids the OPERATOR says are starred right now. Read from the last
   * `GetAllFavoriteEvents` we saw or fetched — never from our own clicks. */
  starredEventIds(): number[] {
    return this.fresh("events", this.data.events).map((e) => e.event_id);
  }

  isStarred(eventId: number): boolean {
    return this.starredEventIds().indexOf(Number(eventId)) >= 0;
  }

  /** Can we offer starring at all? False until the operator's own client has made
   * an authenticated call for us to shadow — so the control appears when it can
   * actually work, and not before. */
  canStar(): boolean {
    return !!this.account?.canCallAccount();
  }

  /**
   * Star or un-star a match on the user's operator account, and report what the
   * OPERATOR ended up holding — not what we asked for.
   *
   * The verification round-trip is not defensive padding. `AddLiveEventFavorite`
   * returns `ok:true, status:200, res:""` for an event id it silently ignores
   * (measured live 2026-08-13). Trusting the 200 would light the ★ on a match that
   * was never saved, which is the same lie the coupon's `Deleted` flag told.
   *
   * Refreshing `events` from the same response also keeps the personalization
   * snapshot honest: starring used to require the user to visit ★ Фавориты before
   * we learned anything, and now every save updates what /suggestions is ranked on.
   */
  async setEventStarred(eventId: number, starred: boolean): Promise<boolean> {
    const id = Number(eventId);
    if (!this.account || !Number.isFinite(id) || id <= 0) return this.isStarred(id);
    const path = starred ? "Account/AddLiveEventFavorite" : "Account/RemoveLiveEventFavorite";
    try {
      await this.account.callAccount(path, { eventNumber: id });
    } catch {
      /* fall through to the read — the write may still have landed */
    }
    if (!this.started) return this.isStarred(id);
    // The ONLY thing that decides the answer.
    try {
      const res = await this.account.callAccount("Account/GetAllFavoriteEvents", {});
      if (!this.started) return this.isStarred(id);
      const events = parseFavouriteEvents(res);
      // An empty list is a real answer here (the user un-starred their last match),
      // and `put` records it as one — see the note on `put`.
      if (Array.isArray(res)) this.put("events", events);
    } catch {
      /* keep whatever we had; the caller re-reads below */
    }
    return this.isStarred(id);
  }

  /** One read of all three lists per page session, once the account is reachable. */
  private maybeFetch(): void {
    if (this.fetchedThisSession || !this.started) return;
    try {
      if (!this.account?.canCallAccount()) return;
    } catch {
      return;
    }
    this.fetchedThisSession = true;
    void this.refreshAll();
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refreshAll();
    }, 1000);
  }

  /** Re-read teams, leagues and events from the account. Failures are silent:
   *  a section we could not read this session is simply not sent. */
  async refreshAll(): Promise<void> {
    const account = this.account;
    if (!account || !this.started) return;
    const reads: Array<[string, keyof Stored, (r: unknown) => unknown[]]> = [
      ["Account/GetAllFavoriteEvents", "events", parseFavouriteEvents],
      ["Account/GetUserFavoriteCompetitors", "teams", parseFavouriteTeams],
      ["Account/GetFavoriteTournament", "leagues", parseFavouriteLeagues],
    ];
    for (const [path, section, parse] of reads) {
      try {
        if (!account.canCallAccount()) return;
        const res = await account.callAccount(path, {});
        if (this.started && Array.isArray(res)) this.put(section, parse(res));
      } catch {
        /* this section stays unread for the session */
      }
    }
  }

  /** One line for the `?bcdiag=1` strip: what we hold and how old it is. */
  diagLine(): string {
    const snap = this.snapshot();
    if (!snap) return "none (open ★ Фавориты once)";
    const ageMin = Math.round((Date.now() - snap.captured_at) / 60000);
    const age = this.liveThisSession ? "live" : `${ageMin}m old`;
    return `teams=${snap.teams.length} leagues=${snap.leagues.length} events=${snap.events.length} ${age}`;
  }
}

export type FavouriteKind = "event" | "team" | "league" | "none";

const norm = (v: unknown): string =>
  v == null ? "" : String(v).trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Is a placed leg on something the visitor starred — narrowest first, the same
 * order and the same keys as the backend's `favourites.match_kind`
 * (product/backend/favourites.py:138): starred fixture, then a starred team by
 * name (a starred fixture's two clubs count as starred teams), then a starred
 * league by name or champ id. Owner 2026-10-12: "add favourites flag on legs too".
 */
export function favouriteKind(
  snap: FavouritesSnapshot,
  leg: { event_id?: number | string | null; home_en?: string; away_en?: string;
         league_en?: string; champ_id?: number },
): FavouriteKind {
  const id = leg.event_id == null ? NaN : Number(leg.event_id);
  if (Number.isFinite(id) && snap.events.some((e) => e.event_id === id)) return "event";
  const teams = new Set<string>();
  for (const t of snap.teams) for (const n of [t.name_en, t.name]) if (norm(n)) teams.add(norm(n));
  for (const e of snap.events) for (const n of [e.home_en, e.away_en, e.home, e.away]) if (norm(n)) teams.add(norm(n));
  if ([leg.home_en, leg.away_en].some((n) => norm(n) && teams.has(norm(n)))) return "team";
  const leagues = new Set<string>();
  for (const l of snap.leagues) for (const n of [l.name_en, l.name]) if (norm(n)) leagues.add(norm(n));
  if (norm(leg.league_en) && leagues.has(norm(leg.league_en))) return "league";
  if (leg.champ_id != null && snap.leagues.some((l) => l.id === leg.champ_id)) return "league";
  return "none";
}
